import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaStickyNote } from 'react-icons/fa';
import { ArrowRight, Calendar as CalendarIcon, Circle, MapPin } from 'lucide-react';
import { RESERVATION_STATUS_LABELS, updateReservation } from '../../../../services/reservationsApi';
import { createServiceReview } from '../../../../services/reviewsApi';
import TableColumnMenu from '../../../../components/TableColumnMenu';
import useTableColumns from '../../../../hooks/useTableColumns';

const SERVICE_STATUS_LABELS = {
  unknown: 'Esperando confirmación del cliente',
  completed: 'Servicio realizado',
  not_completed: 'Servicio no realizado',
};

const REVIEW_FIELDS = [
  { key: 'overall', label: 'Servicio general' },
  { key: 'quality', label: 'Calidad' },
  { key: 'price', label: 'Precio' },
  { key: 'punctuality', label: 'Puntualidad' },
];

const RATING_SCALE = [1, 2, 3, 4, 5];

const REVIEW_INITIAL_STATE = {
  overall: 0,
  quality: 0,
  price: 0,
  punctuality: 0,
  comment: '',
};

const ReviewSummary = ({ review }) => {
  if (!review) return null;
  return (
    <div className="review-summary">
      <div className="review-summary__grid">
        {REVIEW_FIELDS.map(field => (
          <div key={field.key} className="review-summary__item">
            <span>{field.label}</span>
            <strong>{review[field.key] ?? '—'}</strong>
          </div>
        ))}
      </div>
      {review.comment ? (
        <p className="review-summary__comment">“{review.comment}”</p>
      ) : null}
    </div>
  );
};

const formatDate = value => {
  if (!value) return 'Sin definir';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin definir';
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = value => {
  if (!value) return 'Sin registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin registro';
  return date.toLocaleString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getInitials = value => {
  if (!value || typeof value !== 'string') return 'CL';
  const tokens = value.trim().split(/\s+/).slice(0, 2);
  const letters = tokens
    .map(word => word.charAt(0).toUpperCase())
    .filter(Boolean)
    .join('');
  return letters || 'CL';
};

const formatLocationName = value => {
  if (!value || typeof value !== 'string') return '';
  return value
    .trim()
    .split(/\s+/)
    .map(word => {
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const RESERVATION_COLUMN_DEFAULTS = [
  { key: 'publication', label: 'Publicacion', width: 320, minWidth: 240 },
  { key: 'dates', label: 'Fechas', width: 220, minWidth: 200 },
  { key: 'client', label: 'Cliente', width: 220, minWidth: 200 },
  { key: 'status', label: 'Estado', width: 200, minWidth: 180 },
  { key: 'actions', label: 'Acciones', width: 160, minWidth: 140 },
];

export default function ReservationList({
  items = [],
  loading = false,
  emptyMessage = 'No hay reservas registradas.',
  onDecision = () => {},
  onNotesChange = () => {},
  notes = {},
  processing = {},
  onReservationUpdate = () => {},
}) {
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({ key: 'publication', direction: 'asc' });
  const [activeColumnMenu, setActiveColumnMenu] = useState('');
  const [reviewForm, setReviewForm] = useState(REVIEW_INITIAL_STATE);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const columnHeaderRefs = useRef({});
  const columnResizeStateRef = useRef({
    columnKey: '',
    startX: 0,
    startWidth: 0,
  });

  const {
    columns: reservationColumns,
    visibleColumns: visibleReservationColumns,
    moveColumn: moveReservationColumn,
    toggleColumn: toggleReservationColumn,
    setColumnWidth: setReservationColumnWidth,
    resetColumns: resetReservationColumns,
  } = useTableColumns('reservations.provider', RESERVATION_COLUMN_DEFAULTS);

  const selectedReservation = useMemo(
    () => items.find(item => item.id === selectedReservationId) || null,
    [items, selectedReservationId]
  );

  useEffect(() => {
    setReviewError('');
    setReviewSubmitting(false);
    if (!selectedReservation || selectedReservation.providerReview) {
      setReviewForm(REVIEW_INITIAL_STATE);
      return;
    }
    setReviewForm(REVIEW_INITIAL_STATE);
  }, [selectedReservation]);

  const toggleColumnMenu = useCallback(columnKey => {
    setActiveColumnMenu(prev => (prev === columnKey ? '' : columnKey));
  }, []);

  const handleReviewRatingChange = useCallback((field, value) => {
    setReviewForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleReviewCommentChange = useCallback(event => {
    const { value } = event.target;
    setReviewForm(prev => ({
      ...prev,
      comment: value,
    }));
  }, []);

  const handleProviderReviewSubmit = async event => {
    event.preventDefault();
    if (!selectedReservation) return;
    setReviewError('');
    const missingField = REVIEW_FIELDS.find(field => (reviewForm[field.key] || 0) < 1);
    if (missingField) {
      setReviewError('Selecciona una calificación en cada criterio.');
      return;
    }
    setReviewSubmitting(true);
    try {
      const targetUserId = selectedReservation.customer?.id || selectedReservation.createdBy || '';
      if (!targetUserId) {
        throw new Error('No encontramos al cliente para vincular la reseña.');
      }
      const review = await createServiceReview({
        reservationId: selectedReservation.id,
        direction: 'provider_to_client',
        targetUserId,
        overall: reviewForm.overall,
        quality: reviewForm.quality,
        price: reviewForm.price,
        punctuality: reviewForm.punctuality,
        comment: reviewForm.comment,
      });
      const updated = await updateReservation(selectedReservation.id, {
        providerReview: review.id,
      });
      if (typeof onReservationUpdate === 'function') {
        onReservationUpdate(updated);
      }
      setReviewForm(REVIEW_INITIAL_STATE);
    } catch (error) {
      setReviewError(error?.message || 'No pudimos registrar tu reseña. Intenta nuevamente.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleColumnContextMenu = useCallback(
    (event, columnKey) => {
      event.preventDefault();
      event.stopPropagation();
      toggleColumnMenu(columnKey);
    },
    [toggleColumnMenu]
  );

  const handleColumnSort = useCallback(columnKey => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  const handleColumnHeaderKeyDown = useCallback(
    (event, columnKey) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleColumnSort(columnKey);
        return;
      }
      if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        event.preventDefault();
        toggleColumnMenu(columnKey);
      }
    },
    [handleColumnSort, toggleColumnMenu]
  );

  const beginColumnResize = useCallback((clientX, column) => {
    if (typeof clientX !== 'number') return;
    columnResizeStateRef.current = {
      columnKey: column.key,
      startX: clientX,
      startWidth: column.width || column.minWidth || 180,
    };
    if (typeof document !== 'undefined') {
      document.body.classList.add('is-resizing-column');
    }
  }, []);

  const handleColumnResizeMouseDown = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.clientX, column);
    },
    [beginColumnResize]
  );

  const handleColumnResizeTouchStart = useCallback(
    (event, column) => {
      if (!event.touches || event.touches.length !== 1) return;
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.touches[0].clientX, column);
    },
    [beginColumnResize]
  );

  useEffect(() => {
    const stopResizing = () => {
      if (!columnResizeStateRef.current.columnKey) return;
      columnResizeStateRef.current = {
        columnKey: '',
        startX: 0,
        startWidth: 0,
      };
      if (typeof document !== 'undefined') {
        document.body.classList.remove('is-resizing-column');
      }
    };

    const handlePointerMove = event => {
      const { columnKey, startX, startWidth } = columnResizeStateRef.current;
      if (!columnKey) return;
      const clientX =
        'touches' in event ? event.touches?.[0]?.clientX : event.clientX;
      if (typeof clientX !== 'number') return;
      event.preventDefault();
      const delta = clientX - startX;
      setReservationColumnWidth(columnKey, startWidth + delta);
    };

    const handlePointerUp = event => {
      if (!columnResizeStateRef.current.columnKey) return;
      if (event) {
        event.preventDefault();
      }
      stopResizing();
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchend', handlePointerUp);
    window.addEventListener('touchcancel', handlePointerUp);
    window.addEventListener('blur', stopResizing);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchend', handlePointerUp);
      window.removeEventListener('touchcancel', handlePointerUp);
      window.removeEventListener('blur', stopResizing);
      stopResizing();
    };
  }, [setReservationColumnWidth]);

  const handlePageSizeChange = useCallback(event => {
    setPageSize(Number(event.target.value));
  }, []);

  const getSortValue = useCallback(item => {
    if (!item) return '';
    switch (sortConfig.key) {
      case 'publication': {
        const label =
          item.publication?.title ||
          item.unit?.model ||
          item.unit?.tractorType ||
          item.unit?.economicNumber ||
          item.id ||
          '';
        return label.toString().toLowerCase();
      }
      case 'dates': {
        const timestamp = item.requestedFrom ? new Date(item.requestedFrom).getTime() : 0;
        return Number.isNaN(timestamp) ? 0 : timestamp;
      }
      case 'client': {
        const label = item.contactName || item.contactEmail || '';
        return label.toString().toLowerCase();
      }
      case 'status': {
        const label = RESERVATION_STATUS_LABELS[item.status] || item.status || '';
        return label.toString().toLowerCase();
      }
      default:
        return '';
    }
  }, [sortConfig.key]);

  const sortedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const next = [...items];
    if (!sortConfig?.key) return next;
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    next.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        if (valueA === valueB) return 0;
        return valueA > valueB ? direction : -direction;
      }
      const stringA = valueA?.toString().toLowerCase() || '';
      const stringB = valueB?.toString().toLowerCase() || '';
      const comparison = stringA.localeCompare(stringB, 'es', { sensitivity: 'base' });
      if (comparison === 0) return 0;
      return comparison > 0 ? direction : -direction;
    });
    return next;
  }, [getSortValue, items, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(0);
  }, [pageSize, sortConfig]);

  const paginatedItems = useMemo(() => {
    if (loading) return [];
    const start = page * pageSize;
    return sortedItems.slice(start, start + pageSize);
  }, [loading, page, pageSize, sortedItems]);

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index),
    [totalPages]
  );

  const showEmptyState = !loading && items.length === 0;

  const renderPlaceholderRow = useCallback(
    key => (
      <tr className="table-placeholder-empty" key={`placeholder-${key}`}>
        <td colSpan={Math.max(1, visibleReservationColumns.length)} aria-hidden="true">
          &nbsp;
        </td>
      </tr>
    ),
    [visibleReservationColumns.length]
  );

  const noteValueFor = id => {
    if (!id) return '';
    if (Object.prototype.hasOwnProperty.call(notes, id)) {
      return notes[id] ?? '';
    }
    const record = items.find(item => item.id === id);
    return record?.decisionNotes || '';
  };

  const handleCloseModal = () => {
    setSelectedReservationId(null);
  };

  const renderReservationDetailModal = () => {
    if (!selectedReservation) return null;

    const requestedDateLabel = selectedReservation.requestedFrom
      ? selectedReservation.requestedTo
        ? `${formatDate(selectedReservation.requestedFrom)} → ${formatDate(
            selectedReservation.requestedTo
          )}`
        : formatDate(selectedReservation.requestedFrom)
      : 'Por definir';
    const registeredLabel = formatDateTime(selectedReservation.created);
    const updatedLabel = formatDateTime(selectedReservation.updated);
    const decisionLabel = selectedReservation.decisionAt
      ? formatDateTime(selectedReservation.decisionAt)
      : 'Sin fecha';

    const routeOrigin =
      formatLocationName(selectedReservation.pickupLocation) || 'Origen por confirmar';
    const routeDestination =
      formatLocationName(selectedReservation.dropoffLocation) || 'Destino por confirmar';

    const contactName = selectedReservation.contactName || 'Contacto por confirmar';
    const contactEmail = selectedReservation.contactEmail || '';
    const contactPhone = selectedReservation.contactPhone || '';
    const contactInitials = getInitials(contactName);
    const cargoDescription = selectedReservation.cargoDescription || 'Sin descripcion';
    const cargoRequirements = selectedReservation.specialRequirements || '';

    const serviceStatusKey = (selectedReservation.serviceStatus || 'unknown').toLowerCase();
    const serviceStatusLabel =
      SERVICE_STATUS_LABELS[serviceStatusKey] || SERVICE_STATUS_LABELS.unknown;
    const serviceConfirmedAtLabel = selectedReservation.serviceStatusConfirmedAt
      ? formatDateTime(selectedReservation.serviceStatusConfirmedAt)
      : '';
    const providerReviewExists = Boolean(selectedReservation.providerReview);

    const statusLabel =
      RESERVATION_STATUS_LABELS[selectedReservation.status] || selectedReservation.status;
    const isPendingDecision = selectedReservation.status === 'pending';
    const processingState = processing[selectedReservation.id];

    const renderContactDetail = (label, value, hrefPrefix) => {
      if (!value) {
        return <span className="reservation-ticket__contact-missing">Sin {label.toLowerCase()}</span>;
      }
      if (!hrefPrefix) {
        return <span className="reservation-ticket__contact-text">{value}</span>;
      }
      const formattedValue =
        hrefPrefix === 'tel:' ? value.replace(/\s+/g, '') : value;
      return (
        <a
          className="reservation-ticket__contact-link"
          href={`${hrefPrefix}${formattedValue}`}
          rel="noopener noreferrer"
        >
          {value}
        </a>
      );
    };

    return (
      <div className="reservations-modal" role="dialog" aria-modal="true">
        <div className="reservations-modal__backdrop" onClick={handleCloseModal} />
        <div className="reservations-modal__dialog reservation-ticket">
          <header className="reservations-modal__header reservation-ticket__header">
            <div className="reservation-ticket__titles">
              <h3>{selectedReservation.publication?.title || 'Publicacion sin titulo'}</h3>
              <div className="reservation-ticket__subtitle-row">
                <p className="reservation-ticket__subtitle">
                  {selectedReservation.unit?.model ||
                    selectedReservation.unit?.tractorType ||
                    selectedReservation.unit?.economicNumber ||
                    'Unidad por asignar'}
                </p>
                <span className={`reservation-ticket__status status-${selectedReservation.status}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="reservations-modal__close reservation-ticket__close"
              onClick={handleCloseModal}
              aria-label="Cerrar detalles"
            >
              ×
            </button>
          </header>

          <div className="reservations-modal__content reservation-ticket__content">
            <section className="reservation-ticket__route-card" aria-label="Ruta y fechas">
              <div className="reservation-ticket__date-row">
                <div className="reservation-ticket__date">
                  <span className="reservation-ticket__icon" aria-hidden="true">
                    <CalendarIcon size={18} strokeWidth={1.8} />
                  </span>
                  <div>
                    <p>Fechas solicitadas</p>
                    <strong>{requestedDateLabel}</strong>
                  </div>
                </div>
                <div className="reservation-ticket__timestamp">
                  <span>Registrada</span>
                  <strong>{registeredLabel}</strong>
                </div>
              </div>

              <div className="reservation-ticket__route-row">
                <div className="reservation-ticket__route-location">
                  <span
                    className="reservation-ticket__route-icon reservation-ticket__route-icon--origin"
                    aria-hidden="true"
                  >
                    <Circle size={16} strokeWidth={1.6} />
                  </span>
                  <div>
                    <p>Origen</p>
                    <strong>{routeOrigin}</strong>
                  </div>
                </div>
                <div className="reservation-ticket__route-connector" aria-hidden="true">
                  <span className="reservation-ticket__route-line" />
                  <ArrowRight size={20} strokeWidth={1.8} />
                  <span className="reservation-ticket__route-line" />
                </div>
                <div className="reservation-ticket__route-location">
                  <span
                    className="reservation-ticket__route-icon reservation-ticket__route-icon--destination"
                    aria-hidden="true"
                  >
                    <MapPin size={18} strokeWidth={1.8} fill="currentColor" />
                  </span>
                  <div>
                    <p>Destino</p>
                    <strong>{routeDestination}</strong>
                  </div>
                </div>
              </div>
            </section>

            <section className="reservation-ticket__grid">
              <div className="reservation-ticket__section">
                <p className="reservation-ticket__section-label">Cliente / Contacto</p>
                <div className="reservation-ticket__contact">
                  <div className="reservation-ticket__avatar" aria-hidden="true">
                    {contactInitials}
                  </div>
                  <div>
                    <p className="reservation-ticket__contact-name">{contactName}</p>
                    <div className="reservation-ticket__contact-details">
                      {renderContactDetail('Correo', contactEmail, 'mailto:')}
                      {renderContactDetail('Teléfono', contactPhone, 'tel:')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="reservation-ticket__section">
                <p className="reservation-ticket__section-label">Mercancía</p>
                <p className="reservation-ticket__description">{cargoDescription}</p>
                {cargoRequirements ? (
                  <p className="reservation-ticket__requirements">
                    <strong>Requisitos especiales:&nbsp;</strong>
                    {cargoRequirements}
                  </p>
                ) : null}
              </div>
            </section>

            <section className="reservation-ticket__notes">
              <div className="reservation-ticket__notes-label">
                <p className="reservation-ticket__section-label">Notas internas</p>
                <span>Visible para el cliente</span>
              </div>
              {isPendingDecision ? (
                <textarea
                  className="reservation-ticket__notes-field"
                  placeholder="Comparte instrucciones o recordatorios para el cliente."
                  value={noteValueFor(selectedReservation.id)}
                  onChange={event => onNotesChange(selectedReservation.id, event.target.value)}
                  rows={3}
                />
              ) : (
                <p className="reservation-ticket__notes-value">
                  {selectedReservation.decisionNotes || 'Sin notas registradas.'}
                </p>
              )}
            </section>

            <section className="reservation-ticket__service">
              <div className="reservation-ticket__service-header">
                <h4>Seguimiento de servicio</h4>
                <span className={`service-status-chip service-status-chip--${serviceStatusKey}`}>
                  {serviceStatusLabel}
                </span>
              </div>
              {selectedReservation.status === 'accepted' ? (
                <p className="reservation-ticket__service-text">
                  Confirma cuando finalices el viaje para solicitar la evaluación del cliente.
                </p>
              ) : null}
              {selectedReservation.status === 'confirmed' && serviceStatusKey === 'unknown' ? (
                <p className="reservation-ticket__service-text">
                  Esperando la respuesta del cliente para desbloquear las reseñas.
                </p>
              ) : null}
              {serviceStatusKey === 'not_completed' ? (
                <p className="reservation-ticket__service-text reservation-ticket__service-text--warning">
                  El cliente indicó que el servicio no se completó.
                </p>
              ) : null}
              {serviceConfirmedAtLabel ? (
                <p className="reservation-ticket__service-meta">
                  Respuesta del cliente: {serviceConfirmedAtLabel}
                </p>
              ) : null}
              {serviceStatusKey === 'completed' ? (
                providerReviewExists ? (
                  <div className="review-card review-card--summary">
                    <h5>Tu evaluación</h5>
                    <ReviewSummary review={selectedReservation.providerReview} />
                  </div>
                ) : (
                  <form className="review-card" onSubmit={handleProviderReviewSubmit}>
                    <h5>Califica al cliente</h5>
                    <p className="review-card__description">
                      Tu evaluación ayuda a mantener la confianza dentro de la red.
                    </p>
                    <div className="review-card__grid">
                      {REVIEW_FIELDS.map(field => (
                        <div key={field.key} className="review-rating-field">
                          <span>{field.label}</span>
                          <div className="review-rating-scale">
                            {RATING_SCALE.map(score => (
                              <button
                                type="button"
                                key={`${field.key}-${score}`}
                                className={`review-rating-btn${
                                  reviewForm[field.key] === score ? ' is-active' : ''
                                }`}
                                aria-pressed={reviewForm[field.key] === score}
                                onClick={() => handleReviewRatingChange(field.key, score)}
                              >
                                {score}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <label className="review-card__comment">
                      Comentario (opcional)
                      <textarea
                        rows={3}
                        value={reviewForm.comment}
                        onChange={handleReviewCommentChange}
                        placeholder="Comparte detalles relevantes de la experiencia."
                      />
                    </label>
                    {reviewError ? <p className="review-card__error">{reviewError}</p> : null}
                    <div className="review-card__actions">
                      <button type="submit" disabled={reviewSubmitting}>
                        {reviewSubmitting ? 'Enviando reseña...' : 'Enviar reseña'}
                      </button>
                    </div>
                  </form>
                )
              ) : null}
            </section>
          </div>

          <footer className="reservations-modal__footer reservation-ticket__footer">
            <div className="reservation-ticket__timestamps">
              <span>Actualizada: {updatedLabel}</span>
              {!isPendingDecision ? <span>Resolución: {decisionLabel}</span> : null}
            </div>
            {isPendingDecision ? (
              <div className="reservation-ticket__actions">
                <button
                  type="button"
                  className="reservation-ticket__btn reservation-ticket__btn--ghost"
                  onClick={() => onDecision(selectedReservation, 'rejected')}
                  disabled={Boolean(processingState)}
                >
                  {processingState === 'rejected' ? 'Rechazando...' : 'Rechazar'}
                </button>
                <button
                  type="button"
                  className="reservation-ticket__btn reservation-ticket__btn--primary"
                  onClick={() => onDecision(selectedReservation, 'accepted')}
                  disabled={Boolean(processingState)}
                >
                  {processingState === 'accepted' ? 'Confirmando...' : 'Aceptar reserva'}
                </button>
              </div>
            ) : (
              <>
                <p className="reservation-ticket__resolved">
                  Resolución registrada el {decisionLabel}.
                </p>
                {selectedReservation.status === 'accepted' ? (
                  <div className="reservation-ticket__actions reservation-ticket__actions--secondary">
                    <button
                      type="button"
                      className="reservation-ticket__btn reservation-ticket__btn--primary"
                      onClick={() => onDecision(selectedReservation, 'confirmed')}
                      disabled={processingState === 'confirmed'}
                    >
                      {processingState === 'confirmed' ? 'Confirmando...' : 'Confirmar servicio'}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </footer>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="table-scroll-hint" role="note">
        <span aria-hidden="true" className="table-scroll-hint__icon" />
        <span className="table-scroll-hint__text">
          Desliza horizontalmente, haz clic en un encabezado para ordenar (A-Z/Z-A), clic derecho para
          configurar columnas o arrastra el borde para ajustar el ancho.
        </span>
      </div>

      <div className="reservations-table-wrapper">
        <table className="reservations-table">
          <thead>
            <tr>
              {visibleReservationColumns.map(column => {
                const isSorted = sortConfig.key === column.key;
                const ariaSort = isSorted
                  ? sortConfig.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';
                return (
                  <th
                    key={column.key}
                    ref={element => {
                      if (element) {
                        columnHeaderRefs.current[column.key] = element;
                      }
                    }}
                    style={{ minWidth: `${column.width}px`, width: `${column.width}px` }}
                    className="table-column-header"
                    onClick={() => handleColumnSort(column.key)}
                    onContextMenu={event => handleColumnContextMenu(event, column.key)}
                    onKeyDown={event => handleColumnHeaderKeyDown(event, column.key)}
                    tabIndex={0}
                    aria-sort={ariaSort}
                    title="Haz clic para ordenar (A-Z/Z-A). Clic derecho abre la configuracion de columnas."
                  >
                    <span className="table-column-header__label">
                      {column.label}
                      <span
                        className={`table-column-header__sort${
                          isSorted ? ` table-column-header__sort--${sortConfig.direction}` : ''
                        }`}
                        aria-hidden="true"
                      >
                        {isSorted ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                      {isSorted ? (
                        <span className="visually-hidden">
                          {`Ordenado ${
                            sortConfig.direction === 'asc' ? 'ascendente' : 'descendente'
                          }`}
                        </span>
                      ) : null}
                    </span>
                    <span
                      role="presentation"
                      className="table-column-header__resizer"
                      onMouseDown={event => handleColumnResizeMouseDown(event, column)}
                      onTouchStart={event => handleColumnResizeTouchStart(event, column)}
                      aria-hidden="true"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
        <tbody>
          {loading ? (
                Array.from({ length: pageSize }).map((_, index) =>
                  renderPlaceholderRow(`loading-${index}`)
                )
          ) : (
            <>
                  {paginatedItems.map(item => {
                    const statusLabel = RESERVATION_STATUS_LABELS[item.status] || item.status;
                    const statusClass = `status-chip status-${item.status}`;
                    const requestedFrom = formatDate(item.requestedFrom);
                    const requestedTo = item.requestedTo ? formatDate(item.requestedTo) : '';
                    const rangeLabel = requestedTo ? `${requestedFrom} - ${requestedTo}` : requestedFrom;

                    const columnContent = {
                      publication: (
                        <div className="reservations-summary-main">
                          <strong>{item.publication?.title || 'Publicacion sin titulo'}</strong>
                          <span>
                            {item.unit?.model ||
                              item.unit?.tractorType ||
                              item.unit?.economicNumber ||
                              'Unidad por asignar'}
                          </span>
                        </div>
                      ),
                      dates: (
                        <div className="reservations-summary-meta">
                          <strong>{rangeLabel}</strong>
                          <span>Registrada: {formatDateTime(item.created)}</span>
                        </div>
                      ),
                      client: (
                        <div className="reservations-summary-client">
                          <strong>{item.contactName || 'Sin nombre'}</strong>
                          <span>{item.contactEmail || 'Sin correo'}</span>
                        </div>
                      ),
                      status: (
                        <div className="reservations-table-status">
                          <span className={statusClass}>{statusLabel}</span>
                          <time dateTime={item.updated}>
                            Actualizada: {formatDateTime(item.updated)}
                          </time>
                        </div>
                      ),
                      actions: (
                        <button
                          type="button"
                          className="reservations-manage-btn"
                          onClick={event => {
                            event.stopPropagation();
                            setSelectedReservationId(item.id);
                          }}
                        >
                          Gestionar
                        </button>
                      ),
                    };

                    return (
                      <tr
                        key={item.id}
                        className="reservations-row"
                        onClick={() => setSelectedReservationId(item.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedReservationId(item.id);
                          }
                        }}
                        data-clickable="true"
                      >
                        {visibleReservationColumns.map(column => (
                          <td
                            key={`${item.id}-${column.key}`}
                            style={{
                              minWidth: `${column.width}px`,
                              width: `${column.width}px`,
                            }}
                          >
                            {columnContent[column.key]}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
              {showEmptyState ? (
                <tr className="reservations-table-empty">
                  <td colSpan={Math.max(1, visibleReservationColumns.length)}>
                    <FaStickyNote aria-hidden="true" />
                    <p>{emptyMessage}</p>
                  </td>
                </tr>
              ) : null}
            </>
          )}
        </tbody>
      </table>
      </div>
      <div className="reservations-table-card__footer reservations-table-footer">
        <div className="table-pagination">
          <div className="table-pagination__page-size">
            <label>
              Mostrar
              <select value={pageSize} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              reservas
            </label>
          </div>
          <div className="table-pagination__controls" role="navigation" aria-label="Paginacion de reservas">
            <button
              type="button"
              onClick={() => setPage(prev => Math.max(prev - 1, 0))}
              disabled={page === 0}
            >
              Anterior
            </button>
            <div className="table-pagination__pages">
              {pageNumbers.map(number => (
                <button
                  type="button"
                  key={`reservation-page-${number}`}
                  className={number === page ? 'active' : ''}
                  aria-current={number === page ? 'page' : undefined}
                  onClick={() => setPage(number)}
                >
                  {number + 1}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPage(prev => Math.min(prev + 1, totalPages - 1))}
              disabled={page >= totalPages - 1}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
      <TableColumnMenu
        anchorElement={columnHeaderRefs.current[activeColumnMenu] || null}
        activeColumnKey={activeColumnMenu}
        columns={reservationColumns}
        onClose={() => setActiveColumnMenu('')}
        onMove={moveReservationColumn}
        onToggle={toggleReservationColumn}
        onWidthChange={setReservationColumnWidth}
        onReset={resetReservationColumns}
      />

      {renderReservationDetailModal()}
    </>
  );
}
